/**
 * NsectionB9Page
 * B.9.額外性的證明（A6.4-FORM-METH-002 Section 9. Demonstration of additionality）
 * 二選一方式切換 + 動態頁籤 + 各子章節富文本
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB9Service, { NsectionB9Detail } from '../services/nsectionB9Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';

type ApproachType = 'investment_barrier_common' | 'performance_based';

interface TabDef {
  key: string;
  label: string;
  field: string;
}

const NsectionB9Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB9Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 表單狀態
  const [approach, setApproach] = useState<ApproachType | ''>('');
  const [regulatoryAnalysis, setRegulatoryAnalysis] = useState('');
  const [lockingInAvoidance, setLockingInAvoidance] = useState('');
  const [investmentAnalysis, setInvestmentAnalysis] = useState('');
  const [barrierAnalysis, setBarrierAnalysis] = useState('');
  const [commonPracticeAnalysis, setCommonPracticeAnalysis] = useState('');
  const [performanceApproach, setPerformanceApproach] = useState('');

  const [activeTab, setActiveTab] = useState('9.1');

  // 頁籤定義（依方式動態）
  const tabsMode1: TabDef[] = [
    { key: '9.1', label: isZh ? '9.1 法規分析' : '9.1 Regulatory analysis', field: 'regulatory_analysis' },
    { key: '9.2', label: isZh ? '9.2 避免鎖定排放水平' : '9.2 Avoidance of locking-in', field: 'locking_in_avoidance' },
    { key: '9.3.1', label: isZh ? '9.3.1 投資分析' : '9.3.1 Investment analysis', field: 'investment_analysis' },
    { key: '9.3.2', label: isZh ? '9.3.2 障礙分析' : '9.3.2 Barrier analysis', field: 'barrier_analysis' },
    { key: '9.3.3', label: isZh ? '9.3.3 常見做法分析' : '9.3.3 Common practice analysis', field: 'common_practice_analysis' },
  ];
  const tabsMode2: TabDef[] = [
    { key: '9.1', label: isZh ? '9.1 法規分析' : '9.1 Regulatory analysis', field: 'regulatory_analysis' },
    { key: '9.2', label: isZh ? '9.2 避免鎖定排放水平' : '9.2 Avoidance of locking-in', field: 'locking_in_avoidance' },
    { key: '9.4', label: isZh ? '9.4 績效基準法' : '9.4 Performance-based approach', field: 'performance_approach' },
  ];

  const currentTabs = approach === 'performance_based' ? tabsMode2 : tabsMode1;

  // 欄位 getter/setter 映射
  const fieldMap: Record<string, { get: () => string; set: (v: string) => void }> = {
    regulatory_analysis: { get: () => regulatoryAnalysis, set: setRegulatoryAnalysis },
    locking_in_avoidance: { get: () => lockingInAvoidance, set: setLockingInAvoidance },
    investment_analysis: { get: () => investmentAnalysis, set: setInvestmentAnalysis },
    barrier_analysis: { get: () => barrierAnalysis, set: setBarrierAnalysis },
    common_practice_analysis: { get: () => commonPracticeAnalysis, set: setCommonPracticeAnalysis },
    performance_approach: { get: () => performanceApproach, set: setPerformanceApproach },
  };

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NsectionB9Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  useEffect(() => {
    if (!selectedProposalId) {
      setData(null);
      setApproach('');
      setRegulatoryAnalysis(''); setLockingInAvoidance('');
      setInvestmentAnalysis(''); setBarrierAnalysis(''); setCommonPracticeAnalysis('');
      setPerformanceApproach('');
      return;
    }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const result = await nsectionB9Service.getByProposal(selectedProposalId);
        if (result) {
          setData(result);
          setApproach((result.additionality_approach as ApproachType) || '');
          setRegulatoryAnalysis(result.regulatory_analysis || '');
          setLockingInAvoidance(result.locking_in_avoidance || '');
          setInvestmentAnalysis(result.investment_analysis || '');
          setBarrierAnalysis(result.barrier_analysis || '');
          setCommonPracticeAnalysis(result.common_practice_analysis || '');
          setPerformanceApproach(result.performance_approach || '');
        } else {
          setData(null);
          setApproach(''); setRegulatoryAnalysis(''); setLockingInAvoidance('');
          setInvestmentAnalysis(''); setBarrierAnalysis(''); setCommonPracticeAnalysis('');
          setPerformanceApproach('');
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally { setLoading(false); }
    };
    load();
  }, [selectedProposalId, isZh]);

  // 切換方式時，確認 activeTab 存在於新頁籤中
  const handleApproachChange = (newApproach: ApproachType) => {
    setApproach(newApproach);
    const newTabs = newApproach === 'performance_based' ? tabsMode2 : tabsMode1;
    if (!newTabs.find(t => t.key === activeTab)) {
      setActiveTab('9.1');
    }
  };

  // 儲存（依目前頁籤個別儲存，approach 每次都一併送出）
  const handleSave = async () => {
    if (!selectedProposalId) return;
    if (!approach) {
      toast.warning(isZh ? '請先選擇額外性論證方式' : 'Please select an additionality approach first');
      return;
    }
    try {
      setLoading(true); setError(null);
      const currentTab = currentTabs.find(t => t.key === activeTab);
      const updateData: any = { additionality_approach: approach };
      if (currentTab) {
        updateData[currentTab.field] = fieldMap[currentTab.field].get();
      }
      const result = await nsectionB9Service.update(selectedProposalId, updateData);
      setData(result);
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally { setLoading(false); }
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b9', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canUpdate = hasPermission('nsection_b9', 'update');

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="nsection_b9" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}
        </div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div>
          {/* 額外性論證方式選擇 */}
          <div style={{
            background: '#f8f9fa', borderRadius: '8px', padding: '16px 20px', marginBottom: '20px',
            border: '1px solid #e8e8e8'
          }}>
            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '12px' }}>
              {isZh ? '額外性論證方式' : 'Additionality Approach'}
            </div>
            <div style={{ display: 'flex', gap: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: canUpdate ? 'pointer' : 'default', fontSize: '14px' }}>
                <input
                  type="radio"
                  name="approach"
                  checked={approach === 'investment_barrier_common'}
                  onChange={() => handleApproachChange('investment_barrier_common')}
                  disabled={!canUpdate}
                />
                {isZh ? '方式一：投資/障礙/常見做法分析' : 'Option 1: Investment/Barrier/Common practice analysis'}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: canUpdate ? 'pointer' : 'default', fontSize: '14px' }}>
                <input
                  type="radio"
                  name="approach"
                  checked={approach === 'performance_based'}
                  onChange={() => handleApproachChange('performance_based')}
                  disabled={!canUpdate}
                />
                {isZh ? '方式二：績效基準法' : 'Option 2: Performance-based approach'}
              </label>
            </div>
          </div>

          {/* 尚未選擇方式 */}
          {!approach ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
              {isZh ? '請先選擇額外性論證方式' : 'Please select an additionality approach above'}
            </div>
          ) : (
            <>
              {/* 頁籤 */}
              <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '16px', flexWrap: 'wrap' }}>
                {currentTabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: '10px 16px', border: 'none',
                      borderBottom: activeTab === tab.key ? '3px solid #4A90E2' : '3px solid transparent',
                      background: activeTab === tab.key ? '#f0f7ff' : 'transparent',
                      color: activeTab === tab.key ? '#4A90E2' : '#666',
                      fontWeight: activeTab === tab.key ? 600 : 400,
                      cursor: 'pointer', fontSize: '13px', marginBottom: '-2px'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* 各頁籤內容 */}
              {currentTabs.map(tab => {
                if (tab.key !== activeTab) return null;
                const fm = fieldMap[tab.field];
                return (
                  <div key={tab.key} style={{
                    width: '100%', background: '#fff', borderRadius: '10px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px',
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#333', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
                      {tab.label}
                    </div>
                    {canUpdate ? (
                      <RichTextEditor
                        value={fm.get()}
                        onChange={fm.set}
                        placeholder={isZh ? '請輸入內容...' : 'Enter content...'}
                        minHeight="250px"
                      />
                    ) : (
                      <div
                        style={{ padding: '16px', fontSize: '15px', color: '#333', lineHeight: 1.8, minHeight: '250px' }}
                        dangerouslySetInnerHTML={{ __html: fm.get() || '-' }}
                      />
                    )}

                    {canUpdate && (
                      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '20px', borderTop: '1px solid #f0f0f0', marginTop: '16px' }}>
                        <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                          {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NsectionB9Page;
